// src/components/OrgOnboarding.jsx
// 3-step org setup wizard: Profile · Categories · Guidelines.

import React, { useState as useState_OB } from "react";
import * as PPIcons from "./_icons";
import { Button, Field } from "./_primitives";
import PinchIcon from "./PinchIcon";

const INDUSTRIES = ["Aviation", "Logistics", "Warehousing", "Manufacturing", "Facilities", "Hospitality", "Healthcare", "Events", "Other"];
const TEAM_SIZES = ["1–10", "11–50", "51–200", "201–1000", "1000+"];

const DEFAULT_CATEGORIES = ["Staffing", "Communication", "Equipment", "Safety", "Customer", "Other"];
const DEFAULT_GUIDELINES = [
  "Be specific — name equipment, locations, or shift times.",
  "Stay factual and constructive. This is a working board, not a complaint box.",
  "Describe operational impact, not the person.",
  "Don't share anything personally identifying about colleagues.",
  "If safety is at risk, escalate via radio first, then post.",
];

function OrgOnboarding({ orgId, orgName, onComplete }) {
  const [step, setStep] = useState_OB(0);
  const [industry, setIndustry] = useState_OB("");
  const [region, setRegion] = useState_OB("");
  const [teamSize, setTeamSize] = useState_OB("");
  const [categories, setCategories] = useState_OB(DEFAULT_CATEGORIES);
  const [guidelines, setGuidelines] = useState_OB(DEFAULT_GUIDELINES);

  const canAdvance =
    step === 0 ? (industry && teamSize) :
    step === 1 ? (categories.length >= 3) :
    true;

  const finish = () => onComplete && onComplete({ industry, region, teamSize, categories, guidelines });

  return (
    <div style={{
      minHeight: "100vh", background: "var(--bg)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
    }}>
      <div style={{
        width: "100%", maxWidth: 640, background: "var(--bg-elevated)",
        border: "1px solid var(--border)", borderRadius: "var(--radius-lg)",
        overflow: "hidden", boxShadow: "var(--shadow-lg)",
      }}>
        {/* Progress */}
        <div style={{
          padding: "18px 24px",
          borderBottom: "1px solid var(--divider)",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 9, background: "var(--accent)",
            color: "var(--accent-fg)", display: "grid", placeItems: "center",
          }}>
            <PinchIcon size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Set up {orgName}</div>
            <div style={{ fontSize: 12, color: "var(--text-faint)", fontFamily: "var(--font-mono)" }}>
              Step {step + 1} of 3
            </div>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 32, height: 4, borderRadius: 2,
                background: i <= step ? "var(--accent)" : "var(--border)",
              }} />
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: 28, minHeight: 360 }}>
          {step === 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-strong)", letterSpacing: "-0.01em" }}>
                  Tell us about your operation
                </h2>
                <p style={{ fontSize: 13, color: "var(--text-dim)", marginTop: 6 }}>
                  We'll tune AI suggestions to fit your context.
                </p>
              </div>
              <Field label="Industry">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                  {INDUSTRIES.map(i => (
                    <button key={i} type="button" onClick={() => setIndustry(i)}
                      style={{
                        padding: "10px 12px", borderRadius: "var(--radius-sm)",
                        border: "1.5px solid",
                        borderColor: industry === i ? "var(--accent)" : "var(--border)",
                        background: industry === i ? "var(--accent-soft)" : "var(--surface)",
                        color: industry === i ? "var(--accent)" : "var(--text)",
                        fontWeight: 600, fontSize: 13, cursor: "pointer",
                        textAlign: "left",
                      }}>{i}</button>
                  ))}
                </div>
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Region">
                  <input className="pp-input" placeholder="North America · West"
                    value={region} onChange={e => setRegion(e.target.value)} />
                </Field>
                <Field label="Team size">
                  <select className="pp-select" value={teamSize} onChange={e => setTeamSize(e.target.value)}>
                    <option value="">—</option>
                    {TEAM_SIZES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          )}

          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-strong)", letterSpacing: "-0.01em" }}>
                  Pick your categories
                </h2>
                <p style={{ fontSize: 13, color: "var(--text-dim)", marginTop: 6 }}>
                  Submitters tag pinch points with one of these. You can edit any time.
                </p>
              </div>
              <CategoryEditor categories={categories} setCategories={setCategories} />
            </div>
          )}

          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-strong)", letterSpacing: "-0.01em" }}>
                  Set the ground rules
                </h2>
                <p style={{ fontSize: 13, color: "var(--text-dim)", marginTop: 6 }}>
                  AI uses these to keep submissions constructive.
                </p>
              </div>
              <GuidelineEditor guidelines={guidelines} setGuidelines={setGuidelines} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 24px", borderTop: "1px solid var(--divider)",
          background: "var(--surface-2)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(s => Math.max(0, s - 1))}>
            ← Back
          </Button>
          {step < 2 ? (
            <Button variant="primary" disabled={!canAdvance} onClick={() => setStep(s => s + 1)}>
              Continue →
            </Button>
          ) : (
            <Button variant="primary" icon={<PPIcons.IconCheck />} onClick={finish}>
              Finish setup
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryEditor({ categories, setCategories }) {
  const [val, setVal] = useState_OB("");
  const add = () => {
    const v = val.trim();
    if (!v || categories.includes(v)) return;
    setCategories([...categories, v]);
    setVal("");
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {categories.map(c => (
          <span key={c} className="pp-chip" style={{
            background: "var(--accent-soft)", color: "var(--accent)",
            border: "1px solid var(--accent-soft-bd)",
            paddingRight: 4, height: 28,
          }}>
            {c}
            <button onClick={() => setCategories(categories.filter(x => x !== c))}
              style={{
                background: "transparent", border: 0, marginLeft: 4,
                padding: 2, cursor: "pointer", color: "currentColor", opacity: 0.7,
                display: "inline-flex", alignItems: "center",
              }}>
              <PPIcons.IconX style={{ width: 12, height: 12 }} />
            </button>
          </span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <input className="pp-input" placeholder="Add a category…"
          value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())} />
        <Button variant="secondary" onClick={add} icon={<PPIcons.IconPlus />}>Add</Button>
      </div>
    </div>
  );
}

function GuidelineEditor({ guidelines, setGuidelines }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {guidelines.map((g, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <div style={{
            width: 22, height: 22, borderRadius: 6, background: "var(--accent-soft)",
            color: "var(--accent)", fontWeight: 700, fontSize: 11,
            display: "grid", placeItems: "center", flexShrink: 0, marginTop: 8,
            fontFamily: "var(--font-mono)",
          }}>{i + 1}</div>
          <textarea
            className="pp-textarea"
            value={g}
            onChange={e => {
              const next = [...guidelines]; next[i] = e.target.value; setGuidelines(next);
            }}
            rows={2}
            style={{ minHeight: 48 }}
          />
          <button onClick={() => setGuidelines(guidelines.filter((_, j) => j !== i))}
            style={{
              background: "transparent", border: 0, padding: 4, cursor: "pointer",
              color: "var(--text-faint)", marginTop: 6,
            }}>
            <PPIcons.IconTrash style={{ width: 14, height: 14 }} />
          </button>
        </div>
      ))}
      <Button variant="ghost" size="sm" icon={<PPIcons.IconPlus />}
        onClick={() => setGuidelines([...guidelines, ""])}>
        Add guideline
      </Button>
    </div>
  );
}

export default OrgOnboarding;
